import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  getProfile,
  updateProfile,
  getProfileStats,
  changePassword,
  deleteAccount,
  getBmiCategory,
  formatDate,
  bloodTypes,
  sexOptions,
  // Weight
  getWeightMeasurements,
  createWeightMeasurement,
  deleteWeightMeasurement,
  // Blood Pressure
  getBloodPressureMeasurements,
  createBloodPressureMeasurement,
  deleteBloodPressureMeasurement,
  // Family
  getFamilyMembers,
  createFamilyMember,
  updateFamilyMember,
  deleteFamilyMember,
  familyRelations,
  commonConditions,
  // Notes
  getHealthNotes,
  createHealthNote,
  updateHealthNote,
  deleteHealthNote,
  noteTypes,
  // Medications
  getMedications,
  createMedication,
  updateMedication,
  deleteMedication,
  medicationTypes,
  type User,
  type ProfileStats,
  type ProfileUpdateData,
  type WeightMeasurement,
  type BloodPressureMeasurement,
  type FamilyMember,
  type HealthNote,
  type Medication,
} from '../api/profile';

type TabType = 'profilo' | 'misurazioni' | 'dati-sanitari' | 'farmaci' | 'famiglia' | 'note' | 'sicurezza';

// Convert ISO date to Italian format (DD/MM/YYYY)
function isoToItalian(isoDate: string): string {
  if (!isoDate) return '';
  const parts = isoDate.split('T')[0].split('-');
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

// Convert Italian format (DD/MM/YYYY) to ISO (YYYY-MM-DD)
function italianToIso(italianDate: string): string {
  if (!italianDate) return '';
  const parts = italianDate.split('/');
  if (parts.length !== 3) return italianDate;
  const [day, month, year] = parts;
  if (day.length === 2 && month.length === 2 && year.length === 4) {
    return `${year}-${month}-${day}`;
  }
  return italianDate;
}

// Get today in Italian format
function getTodayItalian(): string {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
}

// Calculate age from date of birth
function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birth = new Date(dateOfBirth);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

// Italian date input component
function ItalianDateInput({
  value,
  onChange,
  placeholder = 'GG/MM/AAAA',
  className = ''
}: {
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [displayValue, setDisplayValue] = useState(isoToItalian(value));

  useEffect(() => {
    setDisplayValue(isoToItalian(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, '');
    if (input.length > 8) input = input.slice(0, 8);

    let formatted = input;
    if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
    if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);

    setDisplayValue(formatted);

    if (input.length === 8) {
      const day = input.slice(0, 2);
      const month = input.slice(2, 4);
      const year = input.slice(4, 8);
      onChange(`${year}-${month}-${day}`);
    } else if (input.length === 0) {
      onChange('');
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={className}
      />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 opacity-0 cursor-pointer"
        title="Seleziona data"
      />
      <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

export function Profile() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('profilo');

  // Form state
  const [formData, setFormData] = useState<ProfileUpdateData>({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Measurements state
  const [weightMeasurements, setWeightMeasurements] = useState<WeightMeasurement[]>([]);
  const [bpMeasurements, setBpMeasurements] = useState<BloodPressureMeasurement[]>([]);
  const [newWeight, setNewWeight] = useState({ weight: '', date: getTodayItalian(), notes: '' });
  const [newBp, setNewBp] = useState({ systolic: '', diastolic: '', heartRate: '', date: getTodayItalian(), notes: '' });
  const [initialWeightInProfile, setInitialWeightInProfile] = useState<number | null>(null);

  // Family state
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [showFamilyForm, setShowFamilyForm] = useState(false);
  const [editingFamily, setEditingFamily] = useState<FamilyMember | null>(null);
  const [familyForm, setFamilyForm] = useState({ relation: '', birthYear: '', conditions: [] as string[], diagnosisAge: '', notes: '' });
  const [customConditions, setCustomConditions] = useState<string[]>([]);
  const [newCondition, setNewCondition] = useState('');

  // Notes state
  const [healthNotes, setHealthNotes] = useState<HealthNote[]>([]);
  const [noteSearch, setNoteSearch] = useState('');
  const [noteTypeFilter, setNoteTypeFilter] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingNote, setEditingNote] = useState<HealthNote | null>(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '', type: 'general', tags: '' as string, date: getTodayItalian() });

  // Medications state
  const [medications, setMedications] = useState<Medication[]>([]);
  const [showMedForm, setShowMedForm] = useState(false);
  const [editingMed, setEditingMed] = useState<Medication | null>(null);
  const [medForm, setMedForm] = useState({
    name: '', type: 'medication', dosage: '', frequency: '', reason: '',
    startDate: getTodayItalian(), endDate: '', prescriber: '', notes: ''
  });

  // Get latest weight from measurements (sorted by date desc)
  const latestWeight = weightMeasurements.length > 0
    ? weightMeasurements.reduce((latest, m) =>
        new Date(m.date) > new Date(latest.date) ? m : latest
      )
    : null;

  // Check if weight field should be editable (no measurements exist)
  const isWeightEditable = weightMeasurements.length === 0;

  // All available conditions (common + custom from existing family members + user added)
  const allConditions = [...new Set([
    ...commonConditions,
    ...customConditions,
    ...familyMembers.flatMap(m => m.conditions)
  ])].sort();

  const loadMeasurements = useCallback(async () => {
    try {
      const [weightRes, bpRes] = await Promise.all([
        getWeightMeasurements(50),
        getBloodPressureMeasurements(50)
      ]);
      setWeightMeasurements(weightRes.measurements);
      setBpMeasurements(bpRes.measurements);
    } catch (err) {
      console.error('Error loading measurements:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Load measurements on initial load to determine weight editability
    loadMeasurements();
  }, [loadMeasurements]);

  useEffect(() => {
    // Load tab-specific data when tab changes
    if (activeTab === 'misurazioni') {
      loadMeasurements();
    } else if (activeTab === 'famiglia') {
      loadFamilyMembers();
    } else if (activeTab === 'note') {
      loadNotes();
    } else if (activeTab === 'farmaci') {
      loadMedications();
    }
  }, [activeTab, loadMeasurements]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, statsRes] = await Promise.all([getProfile(), getProfileStats()]);
      setUser(profileRes.user);
      setStats(statsRes.stats);
      const profileWeight = profileRes.user.profile?.weight || undefined;
      setInitialWeightInProfile(profileWeight ?? null);
      setFormData({
        name: profileRes.user.name || '',
        dateOfBirth: profileRes.user.profile?.dateOfBirth?.split('T')[0] || '',
        sex: profileRes.user.profile?.sex || '',
        height: profileRes.user.profile?.height || undefined,
        weight: profileWeight,
        bloodType: profileRes.user.profile?.bloodType || '',
        allergies: profileRes.user.profile?.allergies || '',
        chronicConditions: profileRes.user.profile?.chronicConditions || '',
      });
    } catch (err) {
      setError('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const res = await getFamilyMembers();
      setFamilyMembers(res.members);
    } catch (err) {
      console.error('Error loading family members:', err);
    }
  };

  const loadNotes = async () => {
    try {
      const res = await getHealthNotes({ search: noteSearch, type: noteTypeFilter || undefined });
      setHealthNotes(res.notes);
    } catch (err) {
      console.error('Error loading notes:', err);
    }
  };

  const loadMedications = async () => {
    try {
      const res = await getMedications();
      setMedications(res.medications);
    } catch (err) {
      console.error('Error loading medications:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // If weight is editable and was changed, also create a measurement
      if (isWeightEditable && formData.weight && formData.weight !== initialWeightInProfile) {
        await createWeightMeasurement({
          weight: formData.weight,
          date: new Date().toISOString().split('T')[0],
          notes: 'Inserito dal profilo'
        });
        await loadMeasurements();
      }

      const result = await updateProfile(formData);
      setUser(result.user);
      setSuccess('Profilo aggiornato con successo');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Errore nel salvataggio del profilo');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setSuccess('Password aggiornata con successo');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel cambio password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      await logout();
      navigate('/login');
    } catch (err) {
      setError('Errore nella cancellazione dell\'account');
    }
  };

  // Weight handlers
  const handleAddWeight = async () => {
    if (!newWeight.weight) return;
    try {
      const isoDate = italianToIso(newWeight.date);
      await createWeightMeasurement({
        weight: parseFloat(newWeight.weight),
        date: isoDate,
        notes: newWeight.notes || undefined
      });
      setNewWeight({ weight: '', date: getTodayItalian(), notes: '' });
      await loadMeasurements();
      // Update profile weight
      await updateProfile({ weight: parseFloat(newWeight.weight) });
      await loadData();
      setSuccess('Peso registrato');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nella registrazione del peso');
    }
  };

  const handleDeleteWeight = async (id: number) => {
    try {
      await deleteWeightMeasurement(id);
      await loadMeasurements();

      // If this was the only measurement, clear profile weight
      const updatedMeasurements = weightMeasurements.filter(m => m.id !== id);
      if (updatedMeasurements.length === 0) {
        await updateProfile({ weight: null });
        await loadData();
      } else {
        // Update profile with the new latest weight
        const newLatest = updatedMeasurements.reduce((latest, m) =>
          new Date(m.date) > new Date(latest.date) ? m : latest
        );
        await updateProfile({ weight: newLatest.weight });
        await loadData();
      }
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  // Blood Pressure handlers
  const handleAddBp = async () => {
    if (!newBp.systolic || !newBp.diastolic) return;
    try {
      const isoDate = italianToIso(newBp.date);
      await createBloodPressureMeasurement({
        systolic: parseInt(newBp.systolic),
        diastolic: parseInt(newBp.diastolic),
        heartRate: newBp.heartRate ? parseInt(newBp.heartRate) : undefined,
        date: isoDate,
        notes: newBp.notes || undefined
      });
      setNewBp({ systolic: '', diastolic: '', heartRate: '', date: getTodayItalian(), notes: '' });
      loadMeasurements();
      setSuccess('Pressione registrata');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nella registrazione della pressione');
    }
  };

  const handleDeleteBp = async (id: number) => {
    try {
      await deleteBloodPressureMeasurement(id);
      loadMeasurements();
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  // Add custom condition
  const handleAddCondition = () => {
    if (newCondition.trim() && !allConditions.includes(newCondition.trim())) {
      setCustomConditions([...customConditions, newCondition.trim()]);
      setFamilyForm({ ...familyForm, conditions: [...familyForm.conditions, newCondition.trim()] });
      setNewCondition('');
    }
  };

  // Family handlers
  const handleSaveFamily = async () => {
    if (!familyForm.relation || familyForm.conditions.length === 0) return;
    try {
      const data = {
        relation: familyForm.relation,
        birthYear: familyForm.birthYear ? parseInt(familyForm.birthYear) : undefined,
        conditions: familyForm.conditions,
        diagnosisAge: familyForm.diagnosisAge ? parseInt(familyForm.diagnosisAge) : undefined,
        notes: familyForm.notes || undefined
      };
      if (editingFamily) {
        await updateFamilyMember(editingFamily.id, data);
      } else {
        await createFamilyMember(data);
      }
      setShowFamilyForm(false);
      setEditingFamily(null);
      setFamilyForm({ relation: '', birthYear: '', conditions: [], diagnosisAge: '', notes: '' });
      setNewCondition('');
      loadFamilyMembers();
      setSuccess(editingFamily ? 'Familiare aggiornato' : 'Familiare aggiunto');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nel salvataggio');
    }
  };

  const handleDeleteFamily = async (id: number) => {
    try {
      await deleteFamilyMember(id);
      loadFamilyMembers();
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  // Notes handlers
  const handleSaveNote = async () => {
    if (!noteForm.content) return;
    try {
      const isoDate = italianToIso(noteForm.date);
      const data = {
        title: noteForm.title || undefined,
        content: noteForm.content,
        type: noteForm.type,
        tags: noteForm.tags ? noteForm.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        date: isoDate
      };
      if (editingNote) {
        await updateHealthNote(editingNote.id, data);
      } else {
        await createHealthNote(data);
      }
      setShowNoteForm(false);
      setEditingNote(null);
      setNoteForm({ title: '', content: '', type: 'general', tags: '', date: getTodayItalian() });
      loadNotes();
      setSuccess(editingNote ? 'Nota aggiornata' : 'Nota creata');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nel salvataggio');
    }
  };

  const handleDeleteNote = async (id: number) => {
    try {
      await deleteHealthNote(id);
      loadNotes();
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  // Medication handlers
  const handleSaveMed = async () => {
    if (!medForm.name || !medForm.startDate) return;
    try {
      const data = {
        name: medForm.name,
        type: medForm.type,
        dosage: medForm.dosage || undefined,
        frequency: medForm.frequency || undefined,
        reason: medForm.reason || undefined,
        startDate: italianToIso(medForm.startDate),
        endDate: medForm.endDate ? italianToIso(medForm.endDate) : undefined,
        prescriber: medForm.prescriber || undefined,
        notes: medForm.notes || undefined
      };
      if (editingMed) {
        await updateMedication(editingMed.id, data);
      } else {
        await createMedication(data);
      }
      setShowMedForm(false);
      setEditingMed(null);
      setMedForm({
        name: '', type: 'medication', dosage: '', frequency: '', reason: '',
        startDate: getTodayItalian(), endDate: '', prescriber: '', notes: ''
      });
      loadMedications();
      setSuccess(editingMed ? 'Farmaco aggiornato' : 'Farmaco aggiunto');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nel salvataggio');
    }
  };

  const handleStopMed = async (med: Medication) => {
    try {
      await updateMedication(med.id, { isActive: false, endDate: new Date().toISOString().split('T')[0] });
      loadMedications();
      setSuccess('Farmaco interrotto');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteMed = async (id: number) => {
    try {
      await deleteMedication(id);
      loadMedications();
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <Layout title="Il Mio Profilo">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg lg:text-xl text-gray-500">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  // Tab order: Profilo, Misurazioni, Dati Sanitari, Farmaci, Famiglia, Note, Sicurezza
  const tabs: { id: TabType; label: string }[] = [
    { id: 'profilo', label: 'Profilo' },
    { id: 'misurazioni', label: 'Misurazioni' },
    { id: 'dati-sanitari', label: 'Dati Sanitari' },
    { id: 'farmaci', label: 'Farmaci' },
    { id: 'famiglia', label: 'Famiglia' },
    { id: 'note', label: 'Note' },
    { id: 'sicurezza', label: 'Sicurezza' },
  ];

  // Prepare chart data
  const weightChartData = [...weightMeasurements]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(m => ({
      date: new Date(m.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      peso: m.weight
    }));

  const bpChartData = [...bpMeasurements]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(m => ({
      date: new Date(m.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      sistolica: m.systolic,
      diastolica: m.diastolic
    }));

  // Display weight: from latest measurement or from profile if no measurements
  const displayWeight = latestWeight?.weight ?? formData.weight;

  return (
    <Layout title="Il Mio Profilo">
      <div className="max-w-5xl mx-auto">
        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-base">
            {error}
            <button onClick={() => setError(null)} className="float-right text-red-500 hover:text-red-700">&times;</button>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-base">
            {success}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 rounded-2xl shadow-sm p-5">
            <p className="text-base text-gray-600 mb-1">Documenti</p>
            <p className="text-3xl font-bold text-blue-600">{stats?.documents || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-2xl shadow-sm p-5">
            <p className="text-base text-gray-600 mb-1">Parametri unici</p>
            <p className="text-3xl font-bold text-purple-600">{stats?.uniqueParameters || 0}</p>
          </div>
          <div className="bg-red-50 rounded-2xl shadow-sm p-5">
            <p className="text-base text-gray-600 mb-1">Fuori Range</p>
            <p className="text-3xl font-bold text-red-600">{stats?.outOfRange || 0}</p>
          </div>
          <div className="bg-green-50 rounded-2xl shadow-sm p-5">
            <p className="text-base text-gray-600 mb-1">Conversazioni</p>
            <p className="text-3xl font-bold text-green-600">{stats?.conversations || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-4 text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* PROFILO TAB */}
            {activeTab === 'profilo' && (
              <div className="space-y-8">
                {/* Info Personali */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Personali</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Nome</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Data di nascita</label>
                      <div className="relative">
                        <ItalianDateInput
                          value={formData.dateOfBirth || ''}
                          onChange={(date) => setFormData({ ...formData, dateOfBirth: date })}
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 pr-20"
                        />
                        {formData.dateOfBirth && (
                          <span className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                            ({calculateAge(formData.dateOfBirth)} anni)
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Sesso</label>
                      <select
                        value={formData.sex || ''}
                        onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleziona...</option>
                        {sexOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Dati Fisici */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Fisici</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Altezza (cm)</label>
                      <input
                        type="number"
                        value={formData.height || ''}
                        onChange={(e) => setFormData({ ...formData, height: e.target.value ? Number(e.target.value) : undefined })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">
                        Peso (kg)
                        {!isWeightEditable && (
                          <span className="text-xs text-gray-400 ml-2">(da Misurazioni)</span>
                        )}
                      </label>
                      {isWeightEditable ? (
                        <input
                          type="number"
                          step="0.1"
                          value={formData.weight || ''}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value ? Number(e.target.value) : undefined })}
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Inserisci peso iniziale"
                        />
                      ) : (
                        <div className="px-4 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 text-gray-700">
                          {displayWeight ? `${displayWeight} kg` : '-'}
                          {latestWeight && (
                            <span className="text-xs text-gray-400 ml-2">
                              ({isoToItalian(latestWeight.date)})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Gruppo Sanguigno</label>
                      <select
                        value={formData.bloodType || ''}
                        onChange={(e) => setFormData({ ...formData, bloodType: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleziona...</option>
                        {bloodTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">BMI</label>
                      {user?.profile?.bmi ? (
                        <div className="px-4 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 flex items-center justify-between">
                          <span className="font-semibold">{user.profile.bmi}</span>
                          <span className={`text-sm px-2 py-1 rounded ${
                            getBmiCategory(user.profile.bmi).color === 'text-green-600' ? 'bg-green-100 text-green-700' :
                            getBmiCategory(user.profile.bmi).color === 'text-yellow-600' ? 'bg-yellow-100 text-yellow-700' :
                            getBmiCategory(user.profile.bmi).color === 'text-orange-600' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {getBmiCategory(user.profile.bmi).label}
                          </span>
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 text-gray-400">
                          Inserisci altezza e peso
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-6 border-t">
                  <p className="text-base text-gray-500">Membro dal {formatDate(user?.createdAt || null)}</p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-3 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </button>
                </div>
              </div>
            )}

            {/* MISURAZIONI TAB */}
            {activeTab === 'misurazioni' && (
              <div className="space-y-8">
                {/* Peso */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Peso</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Form */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="font-medium text-gray-700 mb-3">Registra peso</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Peso (kg)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={newWeight.weight}
                            onChange={(e) => setNewWeight({ ...newWeight, weight: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Data</label>
                          <input
                            type="text"
                            value={newWeight.date}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, '');
                              if (input.length > 8) input = input.slice(0, 8);
                              let formatted = input;
                              if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
                              if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                              setNewWeight({ ...newWeight, date: formatted });
                            }}
                            placeholder="GG/MM/AAAA"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm text-gray-600 mb-1">Note (opzionale)</label>
                        <input
                          type="text"
                          value={newWeight.notes}
                          onChange={(e) => setNewWeight({ ...newWeight, notes: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleAddWeight}
                        disabled={!newWeight.weight}
                        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Registra
                      </button>
                    </div>
                    {/* Chart */}
                    <div>
                      {weightChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={weightChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" fontSize={12} />
                            <YAxis domain={['auto', 'auto']} fontSize={12} />
                            <Tooltip />
                            <Line type="monotone" dataKey="peso" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-gray-400">
                          Nessun dato disponibile
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Recent measurements */}
                  {weightMeasurements.length > 0 && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">Data</th>
                            <th className="px-3 py-2 text-left">Peso</th>
                            <th className="px-3 py-2 text-left">Note</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {weightMeasurements.slice(0, 5).map((m) => (
                            <tr key={m.id} className="border-b">
                              <td className="px-3 py-2">{isoToItalian(m.date)}</td>
                              <td className="px-3 py-2 font-medium">{m.weight} kg</td>
                              <td className="px-3 py-2 text-gray-500">{m.notes || '-'}</td>
                              <td className="px-3 py-2">
                                <button onClick={() => handleDeleteWeight(m.id)} className="text-red-500 hover:text-red-700">Elimina</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Pressione */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pressione Sanguigna</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Form */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="font-medium text-gray-700 mb-3">Registra pressione</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Sistolica (max)</label>
                          <input
                            type="number"
                            value={newBp.systolic}
                            onChange={(e) => setNewBp({ ...newBp, systolic: e.target.value })}
                            placeholder="120"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Diastolica (min)</label>
                          <input
                            type="number"
                            value={newBp.diastolic}
                            onChange={(e) => setNewBp({ ...newBp, diastolic: e.target.value })}
                            placeholder="80"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Battiti/min</label>
                          <input
                            type="number"
                            value={newBp.heartRate}
                            onChange={(e) => setNewBp({ ...newBp, heartRate: e.target.value })}
                            placeholder="72"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm text-gray-600 mb-1">Data</label>
                        <input
                          type="text"
                          value={newBp.date}
                          onChange={(e) => {
                            let input = e.target.value.replace(/\D/g, '');
                            if (input.length > 8) input = input.slice(0, 8);
                            let formatted = input;
                            if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
                            if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                            setNewBp({ ...newBp, date: formatted });
                          }}
                          placeholder="GG/MM/AAAA"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleAddBp}
                        disabled={!newBp.systolic || !newBp.diastolic}
                        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Registra
                      </button>
                    </div>
                    {/* Chart */}
                    <div>
                      {bpChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={bpChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" fontSize={12} />
                            <YAxis domain={['auto', 'auto']} fontSize={12} />
                            <Tooltip />
                            <Line type="monotone" dataKey="sistolica" stroke="#ef4444" strokeWidth={2} name="Sistolica" />
                            <Line type="monotone" dataKey="diastolica" stroke="#3b82f6" strokeWidth={2} name="Diastolica" />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-gray-400">
                          Nessun dato disponibile
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Recent measurements */}
                  {bpMeasurements.length > 0 && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">Data</th>
                            <th className="px-3 py-2 text-left">Pressione</th>
                            <th className="px-3 py-2 text-left">Battiti</th>
                            <th className="px-3 py-2 text-left">Note</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {bpMeasurements.slice(0, 5).map((m) => (
                            <tr key={m.id} className="border-b">
                              <td className="px-3 py-2">{isoToItalian(m.date)}</td>
                              <td className="px-3 py-2 font-medium">{m.systolic}/{m.diastolic} mmHg</td>
                              <td className="px-3 py-2">{m.heartRate || '-'} bpm</td>
                              <td className="px-3 py-2 text-gray-500">{m.notes || '-'}</td>
                              <td className="px-3 py-2">
                                <button onClick={() => handleDeleteBp(m.id)} className="text-red-500 hover:text-red-700">Elimina</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* DATI SANITARI TAB - Now between Misurazioni and Farmaci */}
            {activeTab === 'dati-sanitari' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Allergie</label>
                  <textarea
                    value={formData.allergies || ''}
                    onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                    rows={3}
                    placeholder="Elenca eventuali allergie conosciute..."
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Condizioni croniche</label>
                  <textarea
                    value={formData.chronicConditions || ''}
                    onChange={(e) => setFormData({ ...formData, chronicConditions: e.target.value })}
                    rows={3}
                    placeholder="Elenca eventuali condizioni croniche..."
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="bg-blue-50 p-5 rounded-2xl">
                  <h3 className="font-semibold text-lg text-blue-900 mb-4">Riepilogo dati sanitari</h3>
                  <div className="grid grid-cols-2 gap-4 text-base">
                    <div>
                      <span className="text-blue-700">Ultimo esame:</span>{' '}
                      <span className="text-blue-900 font-medium">
                        {stats?.latestTestDate ? formatDate(stats.latestTestDate) : 'Nessuno'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700">Parametri totali:</span>{' '}
                      <span className="text-blue-900 font-medium">{stats?.parameters || 0}</span>
                    </div>
                    <div>
                      <span className="text-blue-700">Tipi di parametri:</span>{' '}
                      <span className="text-blue-900 font-medium">{stats?.uniqueParameters || 0}</span>
                    </div>
                    <div>
                      <span className="text-blue-700">Fuori range:</span>{' '}
                      <span className={stats?.outOfRange ? 'text-red-600 font-semibold' : 'text-blue-900 font-medium'}>
                        {stats?.outOfRange || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-3 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </button>
                </div>
              </div>
            )}

            {/* FARMACI TAB */}
            {activeTab === 'farmaci' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Farmaci e Integratori</h3>
                  <button
                    onClick={() => {
                      setEditingMed(null);
                      setMedForm({
                        name: '', type: 'medication', dosage: '', frequency: '', reason: '',
                        startDate: getTodayItalian(), endDate: '', prescriber: '', notes: ''
                      });
                      setShowMedForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Aggiungi
                  </button>
                </div>

                {/* Form Modal */}
                {showMedForm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                      <h3 className="text-lg font-semibold mb-4">{editingMed ? 'Modifica Farmaco' : 'Nuovo Farmaco'}</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                            <input
                              type="text"
                              value={medForm.name}
                              onChange={(e) => setMedForm({ ...medForm, name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <select
                              value={medForm.type}
                              onChange={(e) => setMedForm({ ...medForm, type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {medicationTypes.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Dosaggio</label>
                            <input
                              type="text"
                              value={medForm.dosage}
                              onChange={(e) => setMedForm({ ...medForm, dosage: e.target.value })}
                              placeholder="es. 50mg"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Frequenza</label>
                            <input
                              type="text"
                              value={medForm.frequency}
                              onChange={(e) => setMedForm({ ...medForm, frequency: e.target.value })}
                              placeholder="es. 1 volta al giorno"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                          <input
                            type="text"
                            value={medForm.reason}
                            onChange={(e) => setMedForm({ ...medForm, reason: e.target.value })}
                            placeholder="es. Prevenzione emicrania"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Inizio *</label>
                            <input
                              type="text"
                              value={medForm.startDate}
                              onChange={(e) => {
                                let input = e.target.value.replace(/\D/g, '');
                                if (input.length > 8) input = input.slice(0, 8);
                                let formatted = input;
                                if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
                                if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                                setMedForm({ ...medForm, startDate: formatted });
                              }}
                              placeholder="GG/MM/AAAA"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Fine</label>
                            <input
                              type="text"
                              value={medForm.endDate}
                              onChange={(e) => {
                                let input = e.target.value.replace(/\D/g, '');
                                if (input.length > 8) input = input.slice(0, 8);
                                let formatted = input;
                                if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
                                if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                                setMedForm({ ...medForm, endDate: formatted });
                              }}
                              placeholder="GG/MM/AAAA"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Prescrittore</label>
                          <input
                            type="text"
                            value={medForm.prescriber}
                            onChange={(e) => setMedForm({ ...medForm, prescriber: e.target.value })}
                            placeholder="es. Dr. Rossi"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                          <textarea
                            value={medForm.notes}
                            onChange={(e) => setMedForm({ ...medForm, notes: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={handleSaveMed}
                          disabled={!medForm.name || !medForm.startDate}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Salva
                        </button>
                        <button
                          onClick={() => setShowMedForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Medications List */}
                {medications.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>Nessun farmaco registrato</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Active medications */}
                    {medications.filter(m => m.isActive).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-3">In corso</h4>
                        <div className="space-y-3">
                          {medications.filter(m => m.isActive).map((med) => (
                            <div key={med.id} className="border rounded-xl p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">{med.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      med.type === 'supplement' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {medicationTypes.find(t => t.value === med.type)?.label || med.type}
                                    </span>
                                  </div>
                                  {med.dosage && <p className="text-sm text-gray-600">{med.dosage}</p>}
                                  {med.frequency && <p className="text-sm text-gray-500">{med.frequency}</p>}
                                  {med.reason && <p className="text-sm text-gray-500 italic">Motivo: {med.reason}</p>}
                                  <p className="text-xs text-gray-400 mt-1">
                                    Dal {isoToItalian(med.startDate)}
                                    {med.prescriber && ` - ${med.prescriber}`}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingMed(med);
                                      setMedForm({
                                        name: med.name,
                                        type: med.type,
                                        dosage: med.dosage || '',
                                        frequency: med.frequency || '',
                                        reason: med.reason || '',
                                        startDate: isoToItalian(med.startDate),
                                        endDate: med.endDate ? isoToItalian(med.endDate) : '',
                                        prescriber: med.prescriber || '',
                                        notes: med.notes || ''
                                      });
                                      setShowMedForm(true);
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-700"
                                  >
                                    Modifica
                                  </button>
                                  <button
                                    onClick={() => handleStopMed(med)}
                                    className="text-sm text-orange-600 hover:text-orange-700"
                                  >
                                    Interrompi
                                  </button>
                                  <button
                                    onClick={() => handleDeleteMed(med.id)}
                                    className="text-sm text-red-600 hover:text-red-700"
                                  >
                                    Elimina
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Past medications */}
                    {medications.filter(m => !m.isActive).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-500 mb-3">Conclusi</h4>
                        <div className="space-y-3">
                          {medications.filter(m => !m.isActive).map((med) => (
                            <div key={med.id} className="border rounded-xl p-4 bg-gray-50 opacity-70">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-700">{med.name}</span>
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                                      {medicationTypes.find(t => t.value === med.type)?.label || med.type}
                                    </span>
                                  </div>
                                  {med.dosage && <p className="text-sm text-gray-500">{med.dosage}</p>}
                                  <p className="text-xs text-gray-400 mt-1">
                                    {isoToItalian(med.startDate)} - {med.endDate ? isoToItalian(med.endDate) : 'N/A'}
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleDeleteMed(med.id)}
                                  className="text-sm text-red-500 hover:text-red-700"
                                >
                                  Elimina
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* FAMIGLIA TAB */}
            {activeTab === 'famiglia' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Storia Familiare</h3>
                  <button
                    onClick={() => {
                      setEditingFamily(null);
                      setFamilyForm({ relation: '', birthYear: '', conditions: [], diagnosisAge: '', notes: '' });
                      setNewCondition('');
                      setShowFamilyForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Aggiungi familiare
                  </button>
                </div>

                {/* Summary of conditions */}
                {familyMembers.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-4 mb-6">
                    <h4 className="font-medium text-amber-800 mb-2">Condizioni rilevanti in famiglia</h4>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(familyMembers.flatMap(m => m.conditions))].map((condition) => (
                        <span key={condition} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                          {condition}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form Modal */}
                {showFamilyForm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                      <h3 className="text-lg font-semibold mb-4">{editingFamily ? 'Modifica Familiare' : 'Nuovo Familiare'}</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Relazione *</label>
                          <select
                            value={familyForm.relation}
                            onChange={(e) => setFamilyForm({ ...familyForm, relation: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Seleziona...</option>
                            {familyRelations.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Anno di nascita</label>
                            <input
                              type="number"
                              value={familyForm.birthYear}
                              onChange={(e) => setFamilyForm({ ...familyForm, birthYear: e.target.value })}
                              placeholder="es. 1960"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Eta alla diagnosi</label>
                            <input
                              type="number"
                              value={familyForm.diagnosisAge}
                              onChange={(e) => setFamilyForm({ ...familyForm, diagnosisAge: e.target.value })}
                              placeholder="es. 65"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Condizioni mediche</label>
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {allConditions.map((condition) => (
                              <label key={condition} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={familyForm.conditions.includes(condition)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFamilyForm({ ...familyForm, conditions: [...familyForm.conditions, condition] });
                                    } else {
                                      setFamilyForm({ ...familyForm, conditions: familyForm.conditions.filter(c => c !== condition) });
                                    }
                                  }}
                                  className="rounded text-blue-600"
                                />
                                <span className="text-sm">{condition}</span>
                              </label>
                            ))}
                          </div>
                          {/* Add custom condition */}
                          <div className="mt-3 flex gap-2">
                            <input
                              type="text"
                              value={newCondition}
                              onChange={(e) => setNewCondition(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCondition())}
                              placeholder="Aggiungi altra condizione..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={handleAddCondition}
                              disabled={!newCondition.trim()}
                              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm"
                            >
                              + Aggiungi
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                          <textarea
                            value={familyForm.notes}
                            onChange={(e) => setFamilyForm({ ...familyForm, notes: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      {familyForm.relation && familyForm.conditions.length === 0 && (
                        <p className="text-amber-600 text-sm mt-2">
                          Seleziona almeno una condizione medica per salvare
                        </p>
                      )}
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={handleSaveFamily}
                          disabled={!familyForm.relation || familyForm.conditions.length === 0}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Salva
                        </button>
                        <button
                          onClick={() => setShowFamilyForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Family members list */}
                {familyMembers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>Nessun familiare registrato</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {familyMembers.map((member) => (
                      <div key={member.id} className="border rounded-xl p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-semibold text-gray-900">
                              {familyRelations.find(r => r.value === member.relation)?.label || member.relation}
                            </span>
                            {member.birthYear && (
                              <span className="text-sm text-gray-500 ml-2">
                                (nato/a nel {member.birthYear})
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingFamily(member);
                                setFamilyForm({
                                  relation: member.relation,
                                  birthYear: member.birthYear?.toString() || '',
                                  conditions: member.conditions,
                                  diagnosisAge: member.diagnosisAge?.toString() || '',
                                  notes: member.notes || ''
                                });
                                setNewCondition('');
                                setShowFamilyForm(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Modifica
                            </button>
                            <button
                              onClick={() => handleDeleteFamily(member.id)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                        {member.conditions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {member.conditions.map((condition) => (
                              <span key={condition} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                {condition}
                              </span>
                            ))}
                          </div>
                        )}
                        {member.diagnosisAge && (
                          <p className="text-xs text-gray-500 mt-2">Diagnosi a {member.diagnosisAge} anni</p>
                        )}
                        {member.notes && (
                          <p className="text-sm text-gray-500 mt-2 italic">{member.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* NOTE TAB */}
            {activeTab === 'note' && (
              <div>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={noteSearch}
                      onChange={(e) => setNoteSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadNotes()}
                      placeholder="Cerca nelle note..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={noteTypeFilter}
                    onChange={(e) => {
                      setNoteTypeFilter(e.target.value);
                      setTimeout(loadNotes, 0);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tutti i tipi</option>
                    {noteTypes.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setEditingNote(null);
                      setNoteForm({ title: '', content: '', type: 'general', tags: '', date: getTodayItalian() });
                      setShowNoteForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                  >
                    + Nuova nota
                  </button>
                </div>

                {/* Form Modal */}
                {showNoteForm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                      <h3 className="text-lg font-semibold mb-4">{editingNote ? 'Modifica Nota' : 'Nuova Nota'}</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
                          <input
                            type="text"
                            value={noteForm.title}
                            onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <select
                              value={noteForm.type}
                              onChange={(e) => setNoteForm({ ...noteForm, type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {noteTypes.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                            <input
                              type="text"
                              value={noteForm.date}
                              onChange={(e) => {
                                let input = e.target.value.replace(/\D/g, '');
                                if (input.length > 8) input = input.slice(0, 8);
                                let formatted = input;
                                if (input.length > 2) formatted = input.slice(0, 2) + '/' + input.slice(2);
                                if (input.length > 4) formatted = input.slice(0, 2) + '/' + input.slice(2, 4) + '/' + input.slice(4);
                                setNoteForm({ ...noteForm, date: formatted });
                              }}
                              placeholder="GG/MM/AAAA"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Contenuto *</label>
                          <textarea
                            value={noteForm.content}
                            onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tag (separati da virgola)</label>
                          <input
                            type="text"
                            value={noteForm.tags}
                            onChange={(e) => setNoteForm({ ...noteForm, tags: e.target.value })}
                            placeholder="es. urgente, follow-up"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={handleSaveNote}
                          disabled={!noteForm.content}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Salva
                        </button>
                        <button
                          onClick={() => setShowNoteForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes list */}
                {healthNotes.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>Nessuna nota trovata</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {healthNotes.map((note) => (
                      <div key={note.id} className="border rounded-xl p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            {note.title && <span className="font-semibold text-gray-900">{note.title}</span>}
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              note.type === 'symptom' ? 'bg-red-100 text-red-700' :
                              note.type === 'appointment' ? 'bg-blue-100 text-blue-700' :
                              note.type === 'reminder' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {noteTypes.find(t => t.value === note.type)?.label || note.type}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400">
                              {isoToItalian(note.date)}
                            </span>
                            <button
                              onClick={() => {
                                setEditingNote(note);
                                setNoteForm({
                                  title: note.title || '',
                                  content: note.content,
                                  type: note.type,
                                  tags: note.tags.join(', '),
                                  date: isoToItalian(note.date)
                                });
                                setShowNoteForm(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Modifica
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                        {note.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {note.tags.map((tag) => (
                              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SICUREZZA TAB */}
            {activeTab === 'sicurezza' && (
              <div className="space-y-8">
                {/* Change Password */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Cambia password</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Password attuale</label>
                      <input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Nuova password</label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">Conferma nuova password</label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      onClick={handlePasswordChange}
                      disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                      className="px-6 py-3 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {saving ? 'Aggiornamento...' : 'Aggiorna password'}
                    </button>
                  </div>
                </div>

                {/* Delete Account */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold text-red-600 mb-3">Zona pericolosa</h3>
                  <p className="text-base text-gray-600 mb-5">
                    L'eliminazione dell'account e' irreversibile. Tutti i tuoi dati, inclusi
                    documenti, parametri e conversazioni, saranno eliminati permanentemente.
                  </p>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-6 py-3 text-base border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                    >
                      Elimina account
                    </button>
                  ) : (
                    <div className="p-5 bg-red-50 rounded-2xl border border-red-200">
                      <p className="text-base text-red-700 mb-5">
                        Sei sicuro di voler eliminare il tuo account? Questa azione non puo' essere annullata.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleDeleteAccount}
                          className="px-6 py-3 text-base bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                        >
                          Si, elimina il mio account
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="px-6 py-3 text-base border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
